import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { storePath } from "../lib/storeUrl";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, PackageCheck, CreditCard, Banknote, Trash2, Tag, MessageCircle, QrCode } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, errMsg, fileUrl } from "../lib/api";
import { getCart, saveCart, cartSubtotal } from "../lib/cart";
import { setFavicon } from "../lib/utils";
import { getCustomerToken, setCustomerSession } from "../lib/customer";
import { themeVars, headingFont } from "../components/StoreKit";
import { track } from "../lib/tracking";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function CheckoutPage({ slugOverride = null }) {
  const { slug: routeSlug } = useParams();
  const slug = slugOverride || routeSlug;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cart, setCart] = useState(() => getCart(slug));
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "", address: "", pincode: "", city: "", state: "" });
  const [upiCopied, setUpiCopied] = useState(false);
  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText(pay.upi_id || "");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = pay.upi_id || "";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setUpiCopied(true);
    setTimeout(() => setUpiCopied(false), 2000);
  };
  const [coupon, setCoupon] = useState("");
  const [method, setMethod] = useState("cod");
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [waLink, setWaLink] = useState("");

  useEffect(() => {
    api.get(`/public/store/${slug}`).then((r) => { setData(r.data); const fav = r.data.store?.settings?.favicon_url || r.data.branding?.favicon_url; if (fav) setFavicon(fav); }).catch(() => setError("Store unavailable"));
    const ct = getCustomerToken(slug);
    if (ct) api.get(`/public/store/${slug}/customer/me`, { headers: { Authorization: `Bearer ${ct}` } })
      .then((r) => { const c = r.data.customer; setForm((f) => ({ ...f, name: f.name || c.name || "", email: f.email || c.email || "", phone: f.phone || c.phone || "", address: f.address || c.address || "" })); })
      .catch(() => {});
  }, [slug]);
  useEffect(() => { if (data) track("begin_checkout", { value: cartSubtotal(cart), currency: "INR" }, { scope: "store", slug }); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (placed) track("purchase", { value: placed.total, currency: "INR", items: (placed.items || []).map((i) => ({ id: i.product_id, qty: i.qty })) }, { scope: "store", slug });
  }, [placed]); // eslint-disable-line react-hooks/exhaustive-deps

  const store = data?.store;
  const colors = store?.settings?.colors || data?.theme?.colors || {};
  const vars = themeVars(colors, data?.theme);
  const head = headingFont(data?.theme);
  const shippingFee = Number(store?.settings?.shipping_fee || 0);
  const taxPct = Number(store?.settings?.tax_percent || 0);
  const whatsapp = store?.settings?.whatsapp_number || "";
  const custH = () => { const t = getCustomerToken(slug); return t ? { headers: { Authorization: `Bearer ${t}` } } : undefined; };
  const pay = store?.settings?.payments || {};
  const waDigits = whatsapp.replace(/\D/g, "");
  const methods = [
    { key: "cod", label: "Cash on Delivery", hint: "Pay when your order arrives", icon: Banknote },
    ...(pay.upi_enabled && (pay.upi_id || pay.upi_qr) ? [{ key: "upi", label: "Pay via UPI", hint: "Scan QR · PhonePe, GPay, Paytm", icon: QrCode }] : []),
    ...(pay.gateway_enabled !== false ? [{ key: "online", label: "Pay Online", hint: "Cards · Netbanking · UPI gateway", icon: CreditCard }] : []),
    ...(pay.whatsapp_enabled !== false && waDigits ? [{ key: "whatsapp", label: "Order via WhatsApp", hint: "Send full order to the store's WhatsApp", icon: MessageCircle }] : []),
  ];
  const effMethod = methods.some((m) => m.key === method) ? method : "cod";

  const buildPayload = (pm) => ({
    customer: { ...form, ...(getCustomerToken(slug) || !form.password ? {} : { password: form.password }),
                address: `${form.address}, ${form.city}, ${form.state} - ${form.pincode}` },
    items: cart.map((c) => ({ product_id: c.product_id, qty: c.qty, variation: c.variation })),
    coupon_code: coupon,
    payment_method: pm,
    aff: sessionStorage.getItem(`mkt_aff_${slug}`) || "",
  });
  const handleAccount = (resp) => {
    if (resp.customer_token) {
      setCustomerSession(slug, resp.customer_token, resp.account);
      toast.success("Your account is ready — track this order anytime in My Account");
    }
  };

  const subtotal = cartSubtotal(cart);
  const estShipping = subtotal >= 999 || subtotal === 0 ? 0 : shippingFee;
  const estTax = Math.round(subtotal * taxPct / 100);
  const estTotal = subtotal + estShipping + estTax;

  const removeItem = (key) => { const next = cart.filter((c) => c.key !== key); setCart(next); saveCart(slug, next); };

  const orderViaWhatsApp = async () => {
    for (const [k, label] of [["name", "full name"], ["phone", "phone"], ["address", "address"], ["pincode", "pincode"], ["city", "city"], ["state", "state"]]) {
      if (!form[k].trim()) { toast.error(`Please enter your ${label} first`); return; }
    }
    setBusy(true);
    // iOS Safari blocks window.open() called after an await — open the window
    // synchronously inside the tap gesture, then navigate it once the order exists.
    const waWin = window.open("", "_blank");
    try {
      const { data: resp } = await api.post(`/public/store/${slug}/orders`, buildPayload("whatsapp"), custH());
      const o = resp.order;
      handleAccount(resp);
      const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
      const lines = [
        `*New Order — ${o.order_number}*`, `Store: ${store.name}`, "",
        "Customer:",
        `Name: ${o.customer.name}`, `Phone: ${o.customer.phone}`,
        ...(o.customer.email ? [`Email: ${o.customer.email}`] : []),
        `Address: ${o.customer.address}`, "",
        "Products:",
        ...o.items.map((it) => `• ${it.name}${it.variation ? `\n  Options: ${it.variation}` : ""}\n  Qty: ${it.qty} · Price: ${inr(it.price)} · ${inr(it.price * it.qty)}`),
        "",
        `Subtotal: ${inr(o.subtotal)}`,
        o.shipping ? `Delivery Charges: ${inr(o.shipping)}` : "Delivery Charges: Free",
        ...(o.discount > 0 ? [`Discount${o.coupon ? ` (${o.coupon})` : ""}: -${inr(o.discount)}`] : []),
        ...(o.tax > 0 ? [`Tax: ${inr(o.tax)}`] : []),
        `Total Amount: *${inr(o.total)}*`, "",
        `Order ID: ${o.order_number}`,
      ];
      const waUrl = `https://wa.me/${waDigits}?text=${encodeURIComponent(lines.join("\n"))}`;
      setWaLink(waUrl);
      if (waWin && !waWin.closed) waWin.location.href = waUrl;
      saveCart(slug, []);
      setCart([]);
      setPlaced(o);
      window.scrollTo(0, 0);
    } catch (e2) {
      if (waWin) waWin.close();
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (effMethod === "whatsapp") { orderViaWhatsApp(); return; }
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/public/store/${slug}/orders`, buildPayload(effMethod), custH());
      handleAccount(resp);
      saveCart(slug, []);
      setCart([]);
      if (resp.checkout_url) {
        navigate(`${resp.checkout_url}?back=${storePath(slug)}`);
      } else {
        setPlaced(resp.order);
        window.scrollTo(0, 0);
      }
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;

  /* ===== Order confirmation ===== */
  if (placed) {
    return (
      <div style={vars} className="min-h-screen bg-[var(--sbg)] flex items-center justify-center p-6" data-testid="order-confirmation">
        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg bg-white rounded-2xl border border-black/5 shadow-2xl p-6 sm:p-10 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.15 }}
            className="h-20 w-20 rounded-full mx-auto flex items-center justify-center text-white"
            style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
            <PackageCheck className="h-9 w-9" />
          </motion.div>
          <h1 className={`${head} text-3xl tracking-tight mt-6`} style={{ color: "var(--sp)" }}>Thank you for your order!</h1>
          {placed.payment_status === "pending_verification" ? (
            <div className="mt-3 rounded-xl border-2 border-amber-400/60 bg-amber-50 p-4" data-testid="pending-verification-note">
              <p className="text-sm font-extrabold text-amber-700">Your payment is pending verification.</p>
              <p className="text-xs text-amber-600 mt-1">The store will verify your UPI payment and confirm your order shortly.</p>
            </div>
          ) : (
            <p className="text-sm mt-2" style={{ color: "var(--st)", opacity: 0.6 }}>Thank you, {placed.customer?.name}. Your order is being prepared.</p>
          )}
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="open-whatsapp-link"
              className="mt-5 w-full py-4 rounded-full font-extrabold text-sm text-white flex items-center justify-center gap-2 transition-all active:scale-95"
              style={{ backgroundColor: "#25D366" }}>
              <MessageCircle className="h-5 w-5" /> Open WhatsApp to Send Order
            </a>
          )}
          <div className="mt-6 rounded-xl border border-black/10 divide-y divide-black/5 text-left">
            <div className="flex justify-between px-5 py-3.5">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--st)", opacity: 0.5 }}>Order number</span>
              <span data-testid="order-success-number" className="font-mono font-extrabold" style={{ color: "var(--sp)" }}>{placed.order_number}</span>
            </div>
            {placed.items?.map((it, i) => (
              <div key={i} className="flex justify-between gap-3 px-5 py-3 text-sm">
                <span className="min-w-0 break-words">{it.name} × {it.qty}{it.variation ? ` (${it.variation})` : ""}</span>
                <span className="font-bold shrink-0">{fmtINR(it.price * it.qty)}</span>
              </div>
            ))}
            <div className="flex justify-between px-5 py-3 text-sm"><span style={{ color: "var(--st)", opacity: 0.6 }}>Shipping</span><span className="font-bold">{placed.shipping ? fmtINR(placed.shipping) : "Free"}</span></div>
            {placed.discount > 0 && <div className="flex justify-between px-5 py-3 text-sm text-emerald-600"><span>Discount {placed.coupon && `(${placed.coupon})`}</span><span>-{fmtINR(placed.discount)}</span></div>}
            <div className="flex justify-between px-5 py-4">
              <span className="font-extrabold">Total ({placed.payment_method === "cod" ? "Cash on Delivery" : placed.payment_method === "whatsapp" ? "via WhatsApp" : "Online"})</span>
              <span className="font-display font-extrabold text-xl" style={{ color: "var(--sp)" }}>{fmtINR(placed.total)}</span>
            </div>
          </div>
          {getCustomerToken(slug) ? (
            <Link to={storePath(slug, "/account")} data-testid="my-orders-cta"
              className="block w-full text-center py-3.5 rounded-full font-bold text-sm mb-3 text-white transition-all hover:opacity-90"
              style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
              View in My Account → My Orders
            </Link>
          ) : (
            <Link to={storePath(slug, `/account?mode=signup&email=${encodeURIComponent(placed.customer?.email || "")}`)} data-testid="create-account-cta"
              className="block w-full text-center py-3.5 rounded-full font-bold text-sm mb-3 border-2 transition-all hover:opacity-80"
              style={{ borderColor: "var(--sp)", color: "var(--sp)" }}>
              Create an account to track your orders
            </Link>
          )}
          <Link to={storePath(slug, "")} data-testid="order-continue-shopping"
            className="inline-block mt-7 px-8 py-3.5 rounded-full font-bold text-sm text-white transition-transform active:scale-95"
            style={{ backgroundImage: "linear-gradient(135deg, var(--sp), var(--sa))" }}>
            Continue Shopping
          </Link>
          <p className="text-xs mt-4" style={{ color: "var(--st)", opacity: 0.45 }}>Save your order number to track delivery from the store footer.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ ...vars, fontFamily: "var(--sbfont)" }} className="min-h-screen bg-[var(--sbg)] text-[var(--st)]" data-testid="checkout-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Link to={storePath(slug, "")} data-testid="checkout-back" className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity">
            <ArrowLeft className="h-4 w-4" /> {store.name}
          </Link>
          <span className="ml-auto flex items-center gap-1.5 text-xs font-bold" style={{ color: "var(--st)", opacity: 0.5 }}>
            <ShieldCheck className="h-4 w-4" style={{ color: "var(--sa)" }} /> Secure Checkout
          </span>
        </div>
      </header>

      {cart.length === 0 ? (
        <div className="max-w-md mx-auto text-center py-28 px-6">
          <p className={`${head} text-2xl`} style={{ color: "var(--sp)" }}>Your cart is empty</p>
          <Link to={storePath(slug, "")} data-testid="empty-cart-shop" className="inline-block mt-6 px-8 py-3 rounded-full font-bold text-sm text-white" style={{ backgroundColor: "var(--sp)" }}>
            Browse products
          </Link>
        </div>
      ) : (
        <form onSubmit={placeOrder} className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-10" data-testid="checkout-form">
          {/* Left: details */}
          <div className="space-y-8">
            <div>
              <h1 className={`${head} text-3xl tracking-tight`} style={{ color: "var(--sp)" }}>Checkout</h1>
              <p className="text-sm mt-1" style={{ color: "var(--st)", opacity: 0.55 }}>Complete your order in under a minute.</p>
            </div>

            <div className="bg-white rounded-xl border border-black/5 p-6 space-y-4">
              <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--sa)" }}>1 · Contact</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs font-bold">Full name *</Label>
                  <Input data-testid="checkout-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" /></div>
                <div className="space-y-1.5"><Label className="text-xs font-bold">Phone *</Label>
                  <Input data-testid="checkout-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" className="h-11" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs font-bold">Email</Label>
                <Input data-testid="checkout-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" /></div>
              {!getCustomerToken(slug) && (
                <div className="space-y-1.5"><Label className="text-xs font-bold">Password <span style={{ opacity: 0.45 }}>(optional — instantly creates your account to track orders)</span></Label>
                  <Input data-testid="checkout-password" type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" className="h-11" /></div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-black/5 p-6 space-y-4">
              <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--sa)" }}>2 · Delivery address</p>
              <div className="space-y-1.5"><Label className="text-xs font-bold">Address *</Label>
                <Input data-testid="checkout-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House no, street, area" className="h-11" /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-1.5"><Label className="text-xs font-bold">Pincode *</Label>
                  <Input data-testid="checkout-pincode" required value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="h-11" /></div>
                <div className="space-y-1.5"><Label className="text-xs font-bold">City *</Label>
                  <Input data-testid="checkout-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-11" /></div>
                <div className="space-y-1.5"><Label className="text-xs font-bold">State *</Label>
                  <Input data-testid="checkout-state" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="h-11" /></div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-black/5 p-6 space-y-4">
              <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--sa)" }}>3 · Payment / Order method</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {methods.map((m) => (
                  <button key={m.key} type="button" data-testid={`pay-method-${m.key}`} onClick={() => setMethod(m.key)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all active:scale-[0.98] ${effMethod === m.key ? "border-[var(--sp)]" : "border-black/10 hover:border-black/25"}`}
                    style={effMethod === m.key ? { backgroundColor: "var(--ss)" } : {}}>
                    <m.icon className="h-5 w-5 shrink-0" style={{ color: "var(--sp)" }} />
                    <div><p className="text-sm font-extrabold">{m.label}</p><p className="text-xs" style={{ color: "var(--st)", opacity: 0.55 }}>{m.hint}</p></div>
                  </button>
                ))}
              </div>
              {effMethod === "upi" && (
                <div data-testid="upi-panel" className="rounded-xl border-2 p-5 text-center space-y-3" style={{ borderColor: "var(--sp)", backgroundColor: "var(--ss)" }}>
                  <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--sp)" }}>Manual UPI Payment</p>
                  {pay.upi_qr && <img src={fileUrl(pay.upi_qr)} alt="UPI QR" className="h-44 w-44 mx-auto rounded-lg object-contain bg-white p-2 border border-black/10" data-testid="upi-qr-image" />}
                  {pay.upi_name && <p className="font-extrabold text-sm" data-testid="upi-payee">{pay.upi_name}</p>}
                  {pay.upi_id && (
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <p className="font-mono text-sm font-bold break-all" style={{ color: "var(--sp)" }} data-testid="upi-id">{pay.upi_id}</p>
                      <button type="button" data-testid="upi-copy-button" onClick={copyUpiId}
                        className="px-3 h-8 rounded-full text-xs font-extrabold text-white active:scale-95 transition-transform"
                        style={{ backgroundColor: "var(--sp)" }}>
                        {upiCopied ? "UPI ID Copied ✓" : "Copy UPI ID"}
                      </button>
                    </div>
                  )}
                  <div className="text-xs text-left space-y-1 rounded-lg p-3 border border-black/10" style={{ backgroundColor: "rgba(255,255,255,0.6)", color: "var(--st)" }} data-testid="upi-instructions">
                    <p className="font-bold">How to pay:</p>
                    <p>1. Scan the QR code with another device, or copy the UPI ID above.</p>
                    <p>2. Open PhonePe, Google Pay, Paytm, BHIM or any UPI app.</p>
                    <p>3. Pay exactly <span className="font-extrabold" style={{ color: "var(--sp)" }}>{fmtINR(estTotal)}</span>.</p>
                    <p>4. Come back here and tap "I Have Completed Payment".</p>
                  </div>
                  <p className="text-[11px]" style={{ color: "var(--st)", opacity: 0.6 }}>The store will verify your payment and confirm your order.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: order summary */}
          <div className="lg:sticky lg:top-20 h-fit">
            <div className="bg-white rounded-xl border border-black/5 overflow-hidden" data-testid="order-summary">
              <div className="px-6 py-4 border-b border-black/5" style={{ backgroundColor: "var(--ss)" }}>
                <p className="font-display font-extrabold" style={{ color: "var(--sp)" }}>Order Summary</p>
              </div>
              <div className="p-6 space-y-4 max-h-64 overflow-y-auto">
                {cart.map((c) => (
                  <div key={c.key} className="flex gap-3 items-center">
                    <div className="h-14 w-14 rounded-lg bg-zinc-100 overflow-hidden shrink-0 relative">
                      {c.image && <img src={c.image} alt="" className="h-full w-full object-cover" />}
                      <span className="absolute -top-0 -right-0 h-5 w-5 rounded-full text-[10px] font-extrabold text-white flex items-center justify-center" style={{ backgroundColor: "var(--sp)" }}>{c.qty}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{c.name}</p>
                      {c.variation && <p className="text-[10px]" style={{ color: "var(--st)", opacity: 0.5 }}>{c.variation}</p>}
                      <p className="text-xs" style={{ color: "var(--st)", opacity: 0.55 }}>{fmtINR(c.price)} × {c.qty}</p>
                    </div>
                    <span className="text-sm font-extrabold">{fmtINR(c.price * c.qty)}</span>
                    <button type="button" onClick={() => removeItem(c.key)} className="hover:opacity-100 opacity-40 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="px-6 pb-4">
                <div className="flex gap-2">
                  <Tag className="h-4 w-4 mt-2.5" style={{ color: "var(--sa)" }} />
                  <Input data-testid="checkout-coupon" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code (SAVE10)" className="h-10 font-mono" />
                </div>
              </div>
              <div className="px-6 pb-6 space-y-2 text-sm border-t border-black/5 pt-4">
                <div className="flex justify-between"><span style={{ color: "var(--st)", opacity: 0.6 }}>Subtotal</span><span className="font-bold" data-testid="summary-subtotal">{fmtINR(subtotal)}</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--st)", opacity: 0.6 }}>Shipping</span><span className="font-bold">{estShipping ? fmtINR(estShipping) : "Free"}</span></div>
                {estTax > 0 && <div className="flex justify-between"><span style={{ color: "var(--st)", opacity: 0.6 }}>Tax ({taxPct}%)</span><span className="font-bold">{fmtINR(estTax)}</span></div>}
                <div className="flex justify-between pt-2 border-t border-black/10">
                  <span className="font-extrabold">Total</span>
                  <span className="font-display font-extrabold text-2xl" style={{ color: "var(--sp)" }} data-testid="summary-total">{fmtINR(estTotal)}</span>
                </div>
                <p className="text-[10px]" style={{ color: "var(--st)", opacity: 0.4 }}>Coupon discount is applied by the server at order placement.</p>
              </div>
              <div className="px-6 pb-1 text-center">
                <Link to={storePath(slug, "/account")} className="text-xs font-semibold hover:underline" style={{ color: "var(--sp)" }} data-testid="checkout-login-link">
                  Have an account? Login for faster checkout →
                </Link>
              </div>
              <div className="px-6 pb-6">
                <button type="submit" disabled={busy} data-testid="place-order-button"
                  className="w-full h-13 py-4 rounded-full font-extrabold text-sm text-white transition-all active:scale-95 hover:opacity-90 shadow-lg flex items-center justify-center gap-2 text-center leading-snug"
                  style={{ backgroundImage: effMethod === "whatsapp" ? "none" : "linear-gradient(135deg, var(--sp), var(--sa))", backgroundColor: effMethod === "whatsapp" ? "#25D366" : "transparent" }}>
                  {effMethod === "whatsapp" && <MessageCircle className="h-5 w-5" />}
                  {busy ? "Placing order…" : effMethod === "cod" ? `Place Order · Pay ${fmtINR(estTotal)} on Delivery`
                    : effMethod === "upi" ? `I Have Completed Payment · Place Order`
                    : effMethod === "whatsapp" ? "Place Order · Send on WhatsApp"
                    : `Place Order · Pay ${fmtINR(estTotal)} Online`}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
